<template>
	<div>
		<div
			v-if="widgetActive"
			class="hl-detection-status"
		>
			<template v-if="isCreated || isProcessing">
				<LoadingSpinner
					class="hl-detection__loading-spinner"
					color="light"
				/>
				<template v-if="progressPct === 0">
					{{ $I18n.trans('highlights.audio_detection.tagging.status.starting') }}
				</template>
				<template v-else>
					{{ $I18n.trans('highlights.audio_detection.tagging.status.running') }} ({{ progressPct }}%)
				</template>
			</template>
			<div v-else-if="isCompleted">
				<template v-if="job.createdTags > 0 || job.ratedTags > 0">
					{{ $I18n.trans('highlights.audio_detection.tagging.status.completed') }}
					<Icon
						class="hl-detection-status__icon"
						icon-name="fireworks"
						size="xxxxl"
					/>
					<template v-if="job.createdTags > 0">
						{{ $I18n.trans('highlights.audio_detection.tagging.summary_created', {
							created: job.createdTags,
							duration: getDuration(job.totalDuration, { appendUnit: true })
						}) }}
					</template>
					<template v-if="job.ratedTags > 0">
						{{ $I18n.trans('highlights.audio_detection.tagging.summary_rated', {
							rated: job.ratedTags,
						}) }}
					</template>
				</template>
				<template v-else>
					{{ $I18n.trans('highlights.audio_detection.tagging.no_scenes') }}
				</template>
			</div>
		</div>
		<div
			v-else
			class="hl-detection-settings"
		>
			<Select
				v-model="settings.profile"
				:clearable="false"
				:searchable="false"
				:dark-theme="true"
				name="profile"
				:label="$I18n.trans('commons.profile')"
				:options="profileOptions"
			/>
			<InputToggle
				v-model="settings.gamePhasesOnly"
				:label="$I18n.trans('highlights.audio_detection.tagging.game_phases_only')"
				:info="$I18n.trans('highlights.audio_detection.tagging.game_phases_info')"
			/>
			<InputToggle
				v-if="!hideCreateTags"
				v-model="settings.createTags"
				:label="$I18n.trans('highlights.audio_detection.tagging.create_tags')"
				:info="$I18n.trans('highlights.audio_detection.tagging.create_tags_info')"
			/>
			<div
				v-if="hasExistingTags"
				class="hl-detection-settings__row"
			>
				<div class="hl-detection-settings__column">
					<InputToggle
						v-model="settings.rateExisting"
						:label="$I18n.trans('highlights.audio_detection.tagging.rate_existing')"
					/>
				</div>
				<div
					v-if="settings.rateExisting"
					class="hl-detection-settings__column"
				>
					<InputToggle
						v-model="settings.optimizeDuration"
						:label="$I18n.trans('highlights.audio_detection.tagging.optimize_duration')"
					/>
				</div>
			</div>
		</div>
		<div
			v-if="isFailed"
			class="mb-3"
		>
			{{ $I18n.trans('highlights.audio_detection.tagging.failed') }}
		</div>
		<button
			v-if="isIdle"
			class="button button--responsive button--primary"
			@click="initDetection"
		>
			{{ $I18n.trans('highlights.audio_detection.tagging.start') }}
		</button>
		<button
			v-if="isCreated || isProcessing"
			class="button button--responsive button--primary"
			@click="cancelJob"
		>
			{{ $I18n.trans('highlights.audio_detection.tagging.cancel') }}
		</button>
		<button
			v-else-if="isCompleted"
			class="button button--responsive button--primary"
			@click="close"
		>
			{{ transUcfirst('commons.close') }}
		</button>
	</div>
</template>
<script>
import Select from '@shared/components/Select';
import LoadingSpinner from '@shared/components/LoadingSpinner';
import InputToggle from '@shared/components/Form/InputToggle';
import { mapGetters, mapActions } from 'vuex';
import { getDuration } from '@shared/utils/timeUtils';
import { getPeriods } from '@js/logic/game';

export default {
	name:       'HighlightDetectionWidget',
	components: { LoadingSpinner, InputToggle, Select },
	props:      {
		hideCreateTags: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			settings: {
				gamePhasesOnly:   false,
				enhancedAi:       false,
				profile:          'full',
				createTags:       false,
				rateExisting:     true,
				optimizeDuration: true,
			},
			profileOptions: [
				{
					label: $I18n.trans('highlights.audio_detection.profiles.full'),
					value: 'full',
				},
				{
					label: $I18n.trans('highlights.audio_detection.profiles.audience'),
					value: 'audience',
				},
				{
					label: $I18n.trans('highlights.audio_detection.profiles.referee'),
					value: 'referee',
				},
			],
		};
	},
	computed: {
		...mapGetters({
			state:        'highlights/state',
			job:          'highlights/job',
			isProcessing: 'highlights/isProcessing',
			isCreated:    'highlights/isCreated',
			isCompleted:  'highlights/isCompleted',
			isIdle:       'highlights/isIdle',
			isFailed:     'highlights/isFailed',
			progressPct:  'highlights/progressPct',
			sportName:    'event/sportName',
			event:        'event/event',
			tags:         'event/tags',
		}),
		widgetActive() {
			return this.isCreated || this.isProcessing || this.isCompleted;
		},
		hasExistingTags() {
			return Array.isArray(this.tags) && this.tags.length > 0;
		},
	},
	watch: {
		tags: {
			handler() {
				if (getPeriods({
					event: this.event,
					tags:  this.tags,
				}).filter(period => period.type === 'action').length > 0) {
					this.settings.gamePhasesOnly = true;
				}
			},
			immediate: false,
		},
		isCompleted: {
			handler() {
				this.$emit('completed');
			},
			immediate: false,
		},
	},
	mounted() {
		this.loadJobByEvent();
	},
	methods: {
		...mapActions({
			startDetection: 'highlights/startDetection',
			cancelJob:      'highlights/cancelJob',
			loadJobByEvent: 'highlights/loadJobByEvent',
			close:          'highlights/close',
		}),
		getDuration,
		initDetection: function() {
			this.startDetection(this.settings);
		},
	},
};
</script>

<style lang="scss" scoped>

.hl-detection-status__icon,
.hl-detection__loading-spinner {
	margin: 10px 15px 10px 0;
}

.hl-detection-status {
    display: flex;
    align-items: center;
	margin-bottom: 20px;
}

.hl-detection-settings__row {
	display: flex;
	gap: 16px;
}

.hl-detection-settings__column {
	width: 50%;
}
</style>
